// Slug-naming-convention enforcement (item 62 / delivery #71).
//
// Mirror of `api/internal/handlers/slug.go`. Keep both in lock-step so
// the live form validation matches the server's POST/PATCH check
// exactly — a slug that passes here must pass on the wire.
//
// Rule:
//   ^[a-z][a-z0-9]*(\.[a-z0-9]+)*$
// length 2..63 inclusive.

export const SLUG_PATTERN = /^[a-z][a-z0-9]*(\.[a-z0-9]+)*$/;
export const SLUG_MIN_LEN = 2;
export const SLUG_MAX_LEN = 63;

export const SLUG_RULE_HINT =
  'lowercase letters/digits, dots as separators (e.g. acme.platform). No hyphens, underscores, or leading digits.';

// validateSlug returns null when the slug is compliant, or a short
// human-readable error string describing the first failing rule. The
// strings here are surfaced inline next to the input so keep them
// terse — link to the docs page for the full rule.
export function validateSlug(slug: string): string | null {
  if (slug.length < SLUG_MIN_LEN) {
    return `Must be at least ${SLUG_MIN_LEN} characters`;
  }
  if (slug.length > SLUG_MAX_LEN) {
    return `Must be at most ${SLUG_MAX_LEN} characters (got ${slug.length})`;
  }
  if (!SLUG_PATTERN.test(slug)) {
    return 'Lowercase letters/digits with dots as separators (no hyphens, underscores, leading digits, or special chars)';
  }
  return null;
}

// suggestSlug rewrites common operator typos into a candidate that
// passes validateSlug. Matches the Go-side suggestSlug exactly so
// the inline "Did you mean … ?" suggestion stays stable across the
// wire.
//
// Strategy:
//   1. Lowercase.
//   2. Replace separators (-, _, space, +, :, @, /) with `.`
//   3. Drop everything that's not [a-z0-9.]
//   4. Collapse `..`, trim leading/trailing `.`
//   5. Strip leading digits.
//   6. Truncate to SLUG_MAX_LEN.
// Returns null when no salvage path exists (input is empty or only
// separators / special chars).
export function suggestSlug(slug: string): string | null {
  if (!slug) return null;
  let out = '';
  for (const ch of slug) {
    const lower = ch.toLowerCase();
    if ((lower >= 'a' && lower <= 'z') || (lower >= '0' && lower <= '9')) {
      out += lower;
    } else if (
      lower === '.' || lower === '-' || lower === '_' || lower === ' ' ||
      lower === '+' || lower === ':' || lower === '@' || lower === '/'
    ) {
      out += '.';
    }
  }
  while (out.includes('..')) out = out.replaceAll('..', '.');
  out = trim(out, '.');
  while (out.length > 0 && out[0] >= '0' && out[0] <= '9') {
    out = out.slice(1);
  }
  out = trim(out, '.');
  if (out.length > SLUG_MAX_LEN) {
    out = out.slice(0, SLUG_MAX_LEN);
    out = out.replace(/\.+$/, '');
  }
  if (out.length < SLUG_MIN_LEN) return null;
  if (validateSlug(out) !== null) return null;
  return out;
}

function trim(s: string, ch: string): string {
  let i = 0;
  let j = s.length;
  while (i < j && s[i] === ch) i++;
  while (j > i && s[j - 1] === ch) j--;
  return s.slice(i, j);
}
